import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  WritableSignal,
  inject
} from '@angular/core';
import { JepLayer, LegendParams, PluginIDs } from '@jeppesen-foreflight/jmkw-types';
import { JmkwApiService } from '../../../../jmkw-api.service';
import { SliderComponent } from '../../../../base/slider/slider.component';

@Component({
  selector: 'jmkw-legend-control-bar',
  standalone: true,
  imports: [CommonModule, SliderComponent],
  templateUrl: './legend-control-bar.component.html',
  styleUrls: ['./legend-control-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LegendControlBarComponent {
  private readonly _jmkwApiService = inject(JmkwApiService);

  @Input() legendParams?: LegendParams;
  @Input() id?: string;

  get layer(): JepLayer | void {
    return this._jmkwApiService.getLayerById<JepLayer>(this.id);
  }

  /**
   * Gets the effective opacity for the layer group
   * If there are multiple layers in the group, we need to ensure we're showing the correct opacity
   * Returns the primary layer's opacity or the first available opacity value from related layers
   */
  get opacity(): number | undefined {
    // First try to get opacity from the primary layer
    if (this.layer?.opacity !== undefined) {
      return this.layer.opacity;
    }

    // If primary layer is unavailable or has no opacity, check other layers in the group
    const pluginIds = this.legendParams?.data?.pluginIDs;
    if (pluginIds?.length) {
      for (const pluginID of pluginIds) {
        const pluginLayer =
          this._jmkwApiService.getLayerById<JepLayer>(pluginID);
        if (pluginLayer?.opacity !== undefined) {
          return pluginLayer.opacity;
        }
      }
    }

    // Default opacity if nothing is found
    return 1.0;
  }

  get title(): string | undefined {
    return this.legendParams?.title;
  }

  get info(): WritableSignal<string> | undefined {
    return this.legendParams?.legendTitleBarInfo;
  }

  /**
   * Returns all layers that should be controlled together
   * Includes the primary layer and any plugin layers specified in legendParams
   */
  private getAllGroupLayers(): JepLayer[] {
    const layers: JepLayer[] = [];

    // Add the main layer if it exists
    if (this.layer) {
      layers.push(this.layer);
    }

    // Add layers from related plugins if specified in legendParams
    const pluginIds = this.legendParams?.data?.pluginIDs;
    if (pluginIds?.length) {
      pluginIds.forEach((pluginID: PluginIDs) => {
        const pluginLayer =
          this._jmkwApiService.getLayerById<JepLayer>(pluginID);
        if (pluginLayer) {
          layers.push(pluginLayer);
        }
      });
    }

    return layers;
  }

  private updateLayerProperty(property: keyof JepLayer, value: unknown): void {
    try {
      // Get all layers in the group
      const allLayers = this.getAllGroupLayers();

      // Update the property on all layers
      allLayers.forEach((layer) => {
        (layer as any)[property] = value;
      });
    } catch (error) {
      console.warn(
        `Failed to update layer property ${String(property)}:`,
        error
      );
    }
  }

  close(): void {
    this.updateLayerProperty('visible', false);
  }

  changeOpacity(opacityValue: number): void {
    this.updateLayerProperty('opacity', opacityValue);
  }
}
